import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/AppIcon';
import Header from '../components/ui/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Privacy Policy"
        description="Privacy Policy for EmailValidator email validation service. Learn how we protect your data and privacy when using our email validation platform."
        keywords="privacy policy, email validation privacy, data protection, privacy terms, email validator privacy, user privacy"
        canonical="https://mailvalidator.octalchip.com/privacy"
      />
      
      <Header />
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-8">
            <Link to="/" className="inline-flex items-center text-primary hover:text-primary/80 transition-colors mb-4">
              <Icon name="ArrowLeft" size={16} className="mr-2" />
              Back to Home
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
            <p className="text-gray-600">Last updated: {new Date().toLocaleDateString()}</p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
            <section id="information-we-collect">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Information We Collect</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We collect information you provide directly to us when using our email validation service:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Account information (name, email, password)</li>
                <li>Email addresses you validate through our service</li>
                <li>Usage data and validation results</li>
                <li>Payment information (for premium plans)</li>
                <li>Communication preferences</li>
              </ul>
            </section>

            <section id="how-we-use-information">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">2. How We Use Your Information</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We use the collected information to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Provide and improve our email validation services</li>
                <li>Process your validation requests and return results</li>
                <li>Manage your account and track usage</li>
                <li>Send service-related communications</li>
                <li>Ensure security and prevent fraud</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section id="email-validation-data">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Email Validation Data</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                When you validate email addresses through our service:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>We temporarily process the email addresses you submit</li>
                <li>Validation results are stored for your account history</li>
                <li>We do not use validated emails for marketing purposes</li>
                <li>Email addresses are not shared with third parties</li>
                <li>You retain ownership of your email lists</li>
              </ul>
            </section>

            <section id="data-security">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Data Security</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We implement appropriate security measures to protect your data:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Encryption of data in transit and at rest</li>
                <li>Secure authentication and access controls</li>
                <li>Regular security audits and updates</li>
                <li>Limited access to personal data by authorized personnel</li>
                <li>Secure data centers with physical security</li>
              </ul>
            </section>

            <section id="data-retention">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Data Retention</h2>
              <p className="text-gray-700 leading-relaxed">
                We retain your data only as long as necessary to provide our services and comply with legal obligations. Account data is kept while your account is active, and validation history is retained for your reference.
              </p>
            </section>

            <section id="third-party-services">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Third-Party Services</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We may use third-party services for:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Payment processing (Razorpay)</li>
                <li>Authentication (Supabase)</li>
                <li>Analytics and monitoring</li>
                <li>Customer support tools</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                These services have their own privacy policies, and we recommend reviewing them.
              </p>
            </section>

            <section id="your-rights">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Your Rights</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                You have the right to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Access your personal data</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your data</li>
                <li>Export your data</li>
                <li>Opt out of marketing communications</li>
                <li>Withdraw consent for data processing</li>
              </ul>
            </section>

            <section id="cookies-and-tracking">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Cookies and Tracking</h2>
              <p className="text-gray-700 leading-relaxed">
                We use cookies and similar technologies to improve your experience, analyze usage, and provide personalized content. You can control cookie settings through your browser preferences.
              </p>
            </section>

            <section id="childrens-privacy">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                Our service is not intended for children under 13. We do not knowingly collect personal information from children under 13. If you believe we have collected such information, please contact us.
              </p>
            </section>

            <section id="changes-to-policy">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Changes to This Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this privacy policy from time to time. We will notify you of any material changes by posting the new policy on our website and updating the "Last updated" date.
              </p>
            </section>

            <section id="privacy-contact">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Contact Us</h2>
              <p className="text-gray-700 leading-relaxed">
                If you have questions about this privacy policy or our data practices, please contact us through our support channels.
              </p>
            </section>
          </div>
        </div>
        <Footer />
    </div>
  );
};

export default Privacy; 